import { backend, EditableFormField, FieldId, forceGQLArray, WitsConcept } from "Backend";
import { createAsyncThunk } from "@reduxjs/toolkit";
import type { RootState } from "../../store";
import clone from "just-clone";
import {
  AbortLensDraftDocument,
  AiChatView,
  AiChatWithMessages,
  BeginLensDraftDocument,
  CalcCaseProperty,
  CaseDisplayGroup,
  CaseDisplayGroupPutInput,
  CommitLensDraftDocument,
  CreateCellDocument,
  CreateCellMutation,
  CreateCellMutationVariables,
  CreateChronologyConfigDocument,
  CreateFieldDocument,
  CreateFieldMutation,
  CreateFormPageDocument,
  CreateFormPageMutation,
  CreateReviewConfigDocument,
  CreateSectionDocument,
  CreateSectionMutation,
  CustomCaseProperty,
  CustomDocumentProperty,
  DeleteCaseDisplayGroupDocument,
  DeleteCellDocument,
  DeleteChronologyConfigDocument,
  DeleteChronologyConfigMutation,
  DeleteFieldDocument,
  DeleteFormPageDocument,
  DeleteLensOutcomeStatusConfigurationDocument,
  DeleteProfileGenTestCaseDocument,
  DeleteReviewConfigDocument,
  DeleteSectionDocument,
  FormPage,
  GetAiChatListDocument,
  GetAiChatListQueryVariables,
  GetAiChatWithMessagesDocument,
  GetAllReviewConfigsDocument,
  GetCellAiConfigsDocument,
  GetCellAiConfigsQuery,
  GetCustomPropertiesDocument,
  GetFieldAiConfigsDocument,
  GetFieldAiConfigsQuery,
  GetFormResponseErrorsDocument,
  GetFormResponseErrorsQuery,
  GetFormResponseStatsDocument,
  GetFormResponseStatsQuery,
  GetLensChronologyConfigDocument,
  GetLensChronologyConfigQuery,
  GetLensDraftDiffDocument,
  GetLensEditorLensesDocument,
  GetLensFullStateDocument,
  GetLensFullStateQuery,
  GetPageLabelsDocument,
  GetPageLabelsQuery,
  GetPageLabelStatsDocument,
  IncludesEntity,
  LensChronologyConfigInput,
  LensChronologyConfigView,
  LensOutcomeStatusConfigurationView,
  LensRule,
  LensRuleType,
  LensRuleViewDiff,
  ListTestCasesForFieldDocument,
  PageLabelStats,
  ProfileGenTestCaseView,
  ReviewConfig,
  RuleCategory,
  SetCaseInsightsEnabledDocument,
  SetHiddenDocument,
  SetLiveDocument,
  SetSystemPageLabelGroupsForLensDocument,
  SimpleLensView,
  UpdateCaseDisplayGroupLocationsDocument,
  UpdateCaseDisplayGroupLocationsMutationVariables,
  SystemPageLabelGroupView,
  UpdateCaseDisplayGroupsDocument,
  UpdateCellAiConfigDocument,
  UpdateCellAiConfigMutationVariables,
  UpdateCellDocument,
  UpdateCellMutation,
  UpdateCellRequestClearsInput,
  UpdateCellRequestInput,
  UpdateCellRequestValuesInput,
  UpdateCellsDocument,
  UpdateCellsMutation,
  UpdateChronologyConfigDocument,
  UpdateFieldAiConfigDocument,
  UpdateFieldAiConfigMutationVariables,
  UpdateFieldDocument,
  UpdateFieldMutation,
  UpdateFieldRequestClearsInput,
  UpdateFieldRequestInput,
  UpdateFieldRequestValuesInput,
  UpdateFieldsDocument,
  UpdateFieldsMutation,
  UpdateFormPageDocument,
  UpdateFormPageMutation,
  UpdateLensDraftDocument,
  UpdateLensInfoDocument,
  UpdateLensInfoMutation,
  UpdateLensInfoMutationVariables,
  UpdateProfileGenTestCaseDocument,
  UpdateReviewConfigDocument,
  UpdateSectionRequestInput,
  UpdateSectionsDocument,
  UpdateSectionsMutation,
  UpsertCaseCalcPropertyDocument,
  UpsertCaseDisplayGroupDocument,
  UpsertCaseDisplayGroupMutationVariables,
  UpsertCasePropertyDocument,
  UpsertCasePropertyMutationVariables,
  UpsertDocumentPropertyDocument,
  UpsertDocumentPropertyMutationVariables,
  UpsertLensOutcomeStatusConfigurationDocument,
  UpsertLensOutcomeStatusConfigurationMutationVariables,
  UpsertReviewConfigValuesInput,
  GetCaseDisplayGroupsDocument,
  SetCaseDisplayGroupTableItemMutationVariables,
  SetCaseDisplayGroupTableItemDocument,
  UpsertCaseCalcPropertyMutationVariables,
  SetLensAnalyticsExcludeDocument,
  RenamePropSelectOptionDocument,
  RenamePropSelectOptionMutationVariables,
  PublishFieldDocument,
  PublishFieldMutation,
  CellAiConfigView,
  SetLensSectionBasedReviewEnabledDocument,
  GetLensDocumentStatusDocument,
  LensDocumentStatusNode,
  QueryAiChatsQueryVariables,
  QueryAiChatsDocument,
  GetFieldTagConfigsQuery,
  GetFieldTagConfigsDocument,
  UpsertFieldTagConfigDocument,
  GetTagsDocument,
  TagView,
  UpsertTagDocument,
  TagColor,
  FieldTagConfigView,
  Exact,
  PublishTagsMutation,
  PublishTagsDocument,
} from "generated/graphql";
import gqlClient, { queryOrFail, runGqlMutation, runGqlQuery } from "gqlClient";
import { getSelectedLensRuleType, selectRules, selectSelectedRules } from "./lensEditor.query";
import { enqueueSnackbar } from "notistack";
import { extractGQLError } from "shared/PatternGraphQLError";

interface LensEditorBaseFetchProps {
  lensId: number;
}

const fetchFull = (lensId: number) => {
  return runGqlQuery(GetLensFullStateDocument, { lensId });
};

const thunkName = (name: string) => `lenseditor/${name}`;

export const fetchProfileGenTestCases = createAsyncThunk<
  { testCases: ProfileGenTestCaseView[]; fieldId: FieldId },
  { lensId: number; fieldId: FieldId }
>(thunkName("fetchProfileGenTestCases"), async ({ lensId, fieldId }) => {
  const response = await runGqlQuery(ListTestCasesForFieldDocument, { lensId, fieldId });
  return { testCases: response.lens.aiChat.listTestCases, fieldId: fieldId };
});

export const updateProfileGenTestCase = createAsyncThunk<
  {
    id: number;
    lensId: number;
    fieldId: number;
    expectFailure: boolean;
    expectedResponse: string;
    description?: string;
  },
  {
    id: number;
    lensId: number;
    fieldId: number;
    expectFailure: boolean;
    expectedResponse: string;
    description?: string;
  }
>(thunkName("updateProfileGenTestCase"), async (variables, thunkAPI) =>
  runGqlMutation(UpdateProfileGenTestCaseDocument, variables)
    .then(() => variables)
    .catch((err) => thunkAPI.rejectWithValue(extractGQLError(err)))
);

export const deleteProfileGenTestCase = createAsyncThunk<
  { testCaseId: number; lensId: number; fieldId: number },
  { testCaseId: number; lensId: number; fieldId: number }
>(thunkName("deleteProfileGenTestCase"), async (variables, thunkAPI) =>
  runGqlMutation(DeleteProfileGenTestCaseDocument, variables)
    .then(() => variables)
    .catch((err) => thunkAPI.rejectWithValue(extractGQLError(err)))
);

export const fetchLensEditorLenses = createAsyncThunk<SimpleLensView[]>(
  thunkName("fetchLensEditorLenses"),
  async () => {
    return runGqlQuery(GetLensEditorLensesDocument, {}).then((response) => response.lensesNew);
  }
);
export const fetchReviewConfigs = createAsyncThunk<ReviewConfig[]>(thunkName("fetchReviewConfigs"), async () => {
  return runGqlQuery(GetAllReviewConfigsDocument, {}).then((response) => response.reviewConfigs);
});

export const updateLensInfo = createAsyncThunk<
  UpdateLensInfoMutation["lens"]["updateLens"],
  UpdateLensInfoMutationVariables
>(thunkName("updateLensInfo"), async (params) => {
  return runGqlMutation(UpdateLensInfoDocument, params).then((r) => r.lens.updateLens);
});

export const setHidden = createAsyncThunk<{ lensId: number; hidden: boolean }, { lensId: number; hidden: boolean }>(
  thunkName("toggleHidden"),
  async (params) => {
    return runGqlMutation(SetHiddenDocument, params).then((r) => {
      return { lensId: params.lensId, hidden: r.lens.updateLens.hidden };
    });
  }
);

export const setCaseInsightsEnabled = createAsyncThunk<
  { calcsEnabled: boolean },
  { lensId: number; caseInsightsEnabled: boolean }
>(thunkName("toggleCaseInsightsEnabled"), async (params) => {
  return runGqlMutation(SetCaseInsightsEnabledDocument, params).then((r) => r.lens.updateLens);
});

export const fetchLensAndRules = createAsyncThunk<GetLensFullStateQuery, LensEditorBaseFetchProps>(
  thunkName("fetchLensAndRules"),
  async ({ lensId }) => {
    try {
      const response = await fetchFull(lensId);
      return response;
    } catch (e) {
      enqueueSnackbar("Error loading lens and rules", { variant: "error" });
      throw e;
    }
  }
);

export const fetchFormResponseStats = createAsyncThunk<GetFormResponseStatsQuery, LensEditorBaseFetchProps>(
  thunkName("fetchFormResponseStats"),
  async ({ lensId }) => runGqlQuery(GetFormResponseStatsDocument, { lensId })
);

export const fetchFieldAiConfigs = createAsyncThunk<GetFieldAiConfigsQuery, LensEditorBaseFetchProps>(
  thunkName("fetchFieldAiConfigs"),
  async ({ lensId }) => runGqlQuery(GetFieldAiConfigsDocument, { lensId })
);

export const fetchFieldTagConfigs = createAsyncThunk<GetFieldTagConfigsQuery, LensEditorBaseFetchProps>(
  thunkName("fetchFieldTagConfigs"),
  async ({ lensId }) => runGqlQuery(GetFieldTagConfigsDocument, { lensId })
);

export const fetchCellAiConfigs = createAsyncThunk<GetCellAiConfigsQuery, { fieldId: number; lensId: number }>(
  thunkName("fetchCellAiConfigs"),
  async ({ fieldId, lensId }) => runGqlQuery(GetCellAiConfigsDocument, { fieldId, lensId })
);

export const fetchFormResponseErrors = createAsyncThunk<GetFormResponseErrorsQuery, LensEditorBaseFetchProps>(
  thunkName("fetchFormResponseErrors"),
  async ({ lensId }) => runGqlQuery(GetFormResponseErrorsDocument, { lensId })
);

export const fetchDraftDiff = createAsyncThunk<LensRuleViewDiff[] | null | undefined, LensEditorBaseFetchProps>(
  thunkName("fetchDraftDiff"),
  async ({ lensId }) => {
    const response = await runGqlQuery(GetLensDraftDiffDocument, { lensId });
    return response.lens.draftDiff;
  }
);

export const beginDraft = createAsyncThunk<number, LensEditorBaseFetchProps>(
  thunkName("beginDraft"),
  async ({ lensId }) => {
    const response = await queryOrFail(gqlClient.mutation(BeginLensDraftDocument, { lensId }).toPromise());
    return response.lens.beginLensDraft;
  }
);

interface UpdateDraftProps extends LensEditorBaseFetchProps {
  rules: LensRule[];
  deletes: number[];
}

export const updateDraft = createAsyncThunk<{ rules: LensRule[]; deletes: number[] }, UpdateDraftProps>(
  thunkName("updateDraft"),
  async ({ lensId, rules, deletes }) => {
    const vars = { lensId, rules, deletes };
    await runGqlMutation(UpdateLensDraftDocument, vars);
    return vars;
  }
);

export const abortDraft = createAsyncThunk<GetLensFullStateQuery, LensEditorBaseFetchProps>(
  thunkName("abortDraft"),
  async ({ lensId }) => {
    await runGqlMutation(AbortLensDraftDocument, { lensId });
    const response = await fetchFull(lensId);
    return response;
  }
);

export const updateLive = createAsyncThunk<boolean, { formId: number; live: boolean }>(
  thunkName("updateLive"),
  async ({ formId, live }) => {
    await runGqlMutation(SetLiveDocument, { formId, live });
    return live;
  }
);

export const updateAnalyticsExclude = createAsyncThunk<boolean, { lensId: number; analyticsExclude: boolean }>(
  thunkName("updateAnalyticsExclude"),
  async ({ lensId, analyticsExclude }) => {
    await runGqlMutation(SetLensAnalyticsExcludeDocument, { lensId, analyticsExclude });
    return analyticsExclude;
  }
);

export const updateSectionBasedReviewEnabled = createAsyncThunk<
  boolean,
  { lensId: number; sectionBasedReviewEnabled: boolean }
>(thunkName("updateSectionBasedReviewEnabled"), async ({ lensId, sectionBasedReviewEnabled }) => {
  await runGqlMutation(SetLensSectionBasedReviewEnabledDocument, { lensId, sectionBasedReviewEnabled });
  return sectionBasedReviewEnabled;
});

export const commitDraft = createAsyncThunk<GetLensFullStateQuery, LensEditorBaseFetchProps>(
  thunkName("commitDraft"),
  async ({ lensId }) => {
    await runGqlMutation(CommitLensDraftDocument, { lensId });
    const response = await fetchFull(lensId);
    return response;
  }
);

export const fetchAiChats = createAsyncThunk<AiChatView[], GetAiChatListQueryVariables>(
  "lens/fetchAiChats",
  (variables) => runGqlQuery(GetAiChatListDocument, variables).then((res) => res.lens.aiChat.listChats)
);

export const queryAiChats = createAsyncThunk<AiChatView[], QueryAiChatsQueryVariables>(
  "lens/queryAiChats",
  (variables) => runGqlQuery(QueryAiChatsDocument, variables).then((res) => res.lens.aiChat.queryValueGroups)
);

//don't need the lensId to get the messages
export const fetchAiChatMessages = createAsyncThunk<AiChatWithMessages, { lensId: number; chatId: string }>(
  "lens/fetchAiChatMessages",
  ({ lensId, chatId }) =>
    runGqlQuery(GetAiChatWithMessagesDocument, { lensId, chatId }).then((res) => res.lens.aiChat.chat)
);

export const fetchPageLabelStats = createAsyncThunk<PageLabelStats[], { lensId: number }>(
  "lens/fetchPageLabelStats",
  ({ lensId }) => runGqlQuery(GetPageLabelStatsDocument, { lensId }).then((res) => res.lens.pageLabels.pageLabelCounts)
);

export interface AddNewRuleProps extends LensEditorBaseFetchProps {
  textQuery?: string;
  entityQuery?: IncludesEntity;
  fieldId?: number;
}

const defaultRuleType: LensRuleType = {
  category: RuleCategory.Product,
  subtype: "New Subtype",
  description: "New Description",
};
export const addNewRule = createAsyncThunk<LensRule, AddNewRuleProps>(
  thunkName("addNewRule"),
  async ({ lensId, textQuery, entityQuery, fieldId }, { getState }) => {
    const state = getState() as RootState;
    const selectedLensRuleType = getSelectedLensRuleType(state.lensEditor);

    const ruleType = {
      category: selectedLensRuleType?.category ?? defaultRuleType.category,
      subtype: selectedLensRuleType?.subtype[0] ?? defaultRuleType.subtype,
      description: selectedLensRuleType?.description[0] ?? defaultRuleType.description,
    };

    const newRule: LensRule = {
      ruleId: state.lensEditor.ruleIdSerial + 1,
      category: ruleType.category,
      subtype: ruleType.subtype,
      description: ruleType.description,
      fieldId: fieldId ? fieldId : null,
      textQuery: textQuery ? { text: textQuery, fuzziness: null, phraseSlop: null } : null,
      entityQuery: entityQuery || null,
      weight: 0,
    };
    await runGqlMutation(UpdateLensDraftDocument, { lensId, rules: [newRule], deletes: [] });
    return newRule;
  }
);

export interface UpdateRuleProps extends LensEditorBaseFetchProps {
  ruleId: number;
  updateFn: (rule: LensRule) => void;
}

export const updateRule = createAsyncThunk<LensRule, UpdateRuleProps>(
  thunkName("updateRule"),
  async ({ lensId, ruleId, updateFn }, { getState }) => {
    const state = getState() as RootState;
    const rules = selectRules(state);
    const rule = rules.find((r) => r.ruleId === ruleId);

    if (rule !== undefined) {
      const copy = clone(rule);
      updateFn(copy);
      await runGqlMutation(UpdateLensDraftDocument, { lensId, rules: [copy], deletes: [] });
      return copy;
    } else {
      throw new Error(`Unable to find rule by ruleId: ${ruleId}`);
    }
  }
);

export const updateLensRule = createAsyncThunk<LensRule, { lensId: number; rule: LensRule }>(
  thunkName("updateLensRule"),
  async ({ lensId, rule }) => {
    const copy = clone(rule);
    Object.assign(copy, rule);
    await runGqlMutation(UpdateLensDraftDocument, { lensId, rules: [copy], deletes: [] });
    return copy;
  }
);

export interface FetchConceptsProps {
  conceptIds: string[];
}

export const fetchConcepts = createAsyncThunk<{ desired: string[]; found: WitsConcept[] }, FetchConceptsProps>(
  thunkName("fetchConcepts"),
  async ({ conceptIds }) => {
    const found = await backend.conceptLookup(conceptIds);
    return { desired: conceptIds, found: found };
  }
);

export interface EditAllSelectedRulesProps extends LensEditorBaseFetchProps {
  newCategory: RuleCategory | undefined;
  newSubtype: string | undefined;
  newDescription: string | undefined;
  newFieldId: number | undefined;
  newWeight: number | undefined;
}

export const editAllSelectedRules = createAsyncThunk<EditAllSelectedRulesProps, EditAllSelectedRulesProps>(
  thunkName("editAllSelectedRules"),
  async (props, { getState, dispatch }) => {
    const state = getState() as RootState;
    const selectedRules = selectSelectedRules(state);
    const selectedRulesClones = JSON.parse(JSON.stringify(selectedRules)) as LensRule[];
    const { newCategory, newSubtype, newDescription, newFieldId, lensId, newWeight } = props;
    if (newCategory) {
      selectedRulesClones.forEach((rule) => {
        rule.category = newCategory;
      });
    }
    if (newSubtype) {
      selectedRulesClones.forEach((rule) => {
        rule.subtype = newSubtype;
      });
    }
    if (newDescription) {
      selectedRulesClones.forEach((rule) => {
        rule.description = newDescription;
      });
    }
    if (newFieldId) {
      selectedRulesClones.forEach((rule) => {
        rule.fieldId = newFieldId;
      });
    }
    if (newWeight !== undefined) {
      selectedRulesClones.forEach((rule) => {
        rule.weight = newWeight;
      });
    }
    await dispatch(updateDraft({ lensId, rules: selectedRulesClones, deletes: [] }));
    return {
      lensId,
      newCategory,
      newSubtype,
      newDescription,
      newFieldId,
      newWeight,
    };
  }
);

export const deleteAllSelectedRules = createAsyncThunk<void, LensEditorBaseFetchProps>(
  thunkName("deleteAllSelectedRules"),
  async (props, { getState, dispatch }) => {
    const state = getState() as RootState;
    const selectedRules = selectSelectedRules(state);
    const { lensId } = props;
    await dispatch(updateDraft({ lensId, rules: [], deletes: selectedRules.map((r) => r.ruleId) }));
    return;
  }
);

export const updateCellAiConfig = createAsyncThunk<CellAiConfigView, UpdateCellAiConfigMutationVariables>(
  thunkName("updateCellAiConfig"),
  async (variables, thunkAPI) =>
    await runGqlMutation(UpdateCellAiConfigDocument, variables)
      .then(
        () =>
          variables as Omit<UpdateCellAiConfigMutationVariables, "copoutSelectOptions"> & {
            copoutSelectOptions: string[];
          }
      )
      .catch((e) => thunkAPI.rejectWithValue(extractGQLError(e)))
);

export const updateFieldAiConfig = createAsyncThunk<
  UpdateFieldAiConfigMutationVariables,
  UpdateFieldAiConfigMutationVariables
>(thunkName("updateFieldAiConfig"), async (variables) => {
  await runGqlMutation(UpdateFieldAiConfigDocument, variables);
  return variables;
});

interface AddFormSectionProps {
  formId: number;
  rank: number;
  name: string;
  outcomeEnabled: boolean;
  validationEnabled: boolean;
  formPageId: number;
}

export const addFormSection = createAsyncThunk<CreateSectionMutation, AddFormSectionProps>(
  thunkName("addFormSection"),
  async ({ formId, rank, name, outcomeEnabled, validationEnabled, formPageId }) =>
    await runGqlMutation(CreateSectionDocument, { formId, rank, name, validationEnabled, outcomeEnabled, formPageId })
);

interface AddFormFieldProps {
  field: EditableFormField;
  formId: number;
}

export const addFormField = createAsyncThunk<CreateFieldMutation, AddFormFieldProps>(
  thunkName("addFormField"),
  async ({ field, formId }, thunkAPI) =>
    runGqlMutation(CreateFieldDocument, {
      formId,
      ...field,
      analyticsExclude: field.analyticsExclude === true,
    }).catch((gqlError) => thunkAPI.rejectWithValue(extractGQLError(gqlError)))
);

export const addCell = createAsyncThunk<CreateCellMutation, CreateCellMutationVariables>(
  thunkName("addCell"),
  async (variables, thunkAPI) =>
    runGqlMutation(CreateCellDocument, variables).catch((gqlError) =>
      thunkAPI.rejectWithValue(extractGQLError(gqlError))
    )
);

interface UpdateFormFieldProps {
  id: number;
  values: UpdateFieldRequestValuesInput;
  clears?: UpdateFieldRequestClearsInput;
}

export const updateFormField = createAsyncThunk<UpdateFieldMutation, UpdateFormFieldProps>(
  thunkName("updateFormField"),
  async ({ id, values, clears }, thunkAPI) =>
    runGqlMutation(UpdateFieldDocument, {
      fieldId: id,
      values,
      clears,
    }).catch((gqlError) => thunkAPI.rejectWithValue(extractGQLError(gqlError)))
);

interface UpdateFormFieldsProps {
  fields: UpdateFieldRequestInput[];
}

export const updateFormFields = createAsyncThunk<UpdateFieldsMutation, UpdateFormFieldsProps>(
  thunkName("updateFormFields"),
  async ({ fields }) =>
    runGqlMutation(UpdateFieldsDocument, {
      value: fields,
    })
);

export const createAndAttachReviewConfig = createAsyncThunk<ReviewConfig, { fieldId: number; name: string }>(
  thunkName("createAndAttachReviewConfig"),
  async ({ fieldId, name }, { dispatch }) => {
    const response = await runGqlMutation(CreateReviewConfigDocument, { name });
    const createdConfig: ReviewConfig = response.lens.upsertReviewConfig;
    await dispatch(updateFormField({ id: fieldId, values: { reviewConfigId: createdConfig.id } }));
    return createdConfig;
  }
);

export const updateReviewConfig = createAsyncThunk<ReviewConfig, { reviewConfig: ReviewConfig }>(
  thunkName("updateReviewConfig"),
  async ({ reviewConfig }) => {
    const value: UpsertReviewConfigValuesInput = {
      documentReview: reviewConfig.documentReview,
      excludedClusterTypes: reviewConfig.excludedClusterTypes,
      excludedLabelNames: reviewConfig.excludedLabelNames,
      excludedPageTypes: reviewConfig.excludedPageTypes,
      fixedClusterTypes: reviewConfig.fixedClusterTypes,
      fixedLabelNames: reviewConfig.fixedLabelNames,
      fixedPageTypes: reviewConfig.fixedPageTypes,
      id: reviewConfig.id,
      medicationReview: reviewConfig.medicationReview,
      name: reviewConfig.name,
      showCancer: reviewConfig.showCancer,
      showFamily: reviewConfig.showFamily,
      showStage: reviewConfig.showStage,
      hideNegated: reviewConfig.hideNegated,
      suggestedClusterTypes: reviewConfig.suggestedClusterTypes,
      suggestedLabelNames: reviewConfig.suggestedLabelNames,
      suggestedPageTypes: reviewConfig.suggestedPageTypes,
      distinctMode: reviewConfig.distinctMode,
      documentRequiredProperties: reviewConfig.requiredCustomProperties,
      documentExcludedProperties: reviewConfig.excludedCustomProperties,
    };
    return runGqlMutation(UpdateReviewConfigDocument, { value }).then((response) => response.lens.upsertReviewConfig);
  }
);

export const deleteReviewConfig = createAsyncThunk<{ configId: number }, { configId: number }>(
  "lensEditor/deleteReviewConfig",
  async ({ configId }) =>
    await runGqlMutation(DeleteReviewConfigDocument, { value: configId }).then(() => ({ configId }))
);

interface UpdateFormSectionsProps {
  sections: UpdateSectionRequestInput[];
}

export const updateFormSections = createAsyncThunk<UpdateSectionsMutation, UpdateFormSectionsProps>(
  thunkName("updateFormSections"),
  async ({ sections }) =>
    runGqlMutation(UpdateSectionsDocument, {
      value: sections,
    })
);

interface UpdateCellProps {
  id: number;
  values: UpdateCellRequestValuesInput;
  clears: UpdateCellRequestClearsInput;
}

export const updateCell = createAsyncThunk<UpdateCellMutation, UpdateCellProps>(
  thunkName("updateCell"),
  async ({ id, values, clears }, thunkAPI) =>
    runGqlMutation(UpdateCellDocument, {
      cellId: id,
      values,
      clears,
    }).catch((e) => thunkAPI.rejectWithValue(extractGQLError(e)))
);

interface UpdateCellsProps {
  cells: UpdateCellRequestInput[];
}

export const updateCells = createAsyncThunk<UpdateCellsMutation, UpdateCellsProps>(
  thunkName("updateCells"),
  async ({ cells }) => runGqlMutation(UpdateCellsDocument, { value: cells })
);

export const deleteField = createAsyncThunk<{ fieldId: number }, { fieldId: number }>(
  thunkName("deleteFormField"),
  async ({ fieldId }) => runGqlMutation(DeleteFieldDocument, { fieldId }).then(() => ({ fieldId }))
);

export const publishField = createAsyncThunk<
  PublishFieldMutation,
  { lensId: number; fieldId: number; dryrun: boolean; onlyPublishLensIds: number[] }
>(thunkName("publishField"), async ({ lensId, fieldId, dryrun, onlyPublishLensIds }, thunkAPI) =>
  runGqlMutation(PublishFieldDocument, { lensId, fieldId, dryrun, onlyPublishLensIds: onlyPublishLensIds }).catch((e) =>
    thunkAPI.rejectWithValue(extractGQLError(e))
  )
);

export const publishTags = createAsyncThunk<
  PublishTagsMutation,
  { lensId: number; dryrun: boolean; onlyPublishLensIds: number[] }
>(thunkName("publishTags"), async ({ lensId, dryrun, onlyPublishLensIds }, thunkAPI) =>
  runGqlMutation(PublishTagsDocument, { lensId, dryrun, onlyPublishLensIds: onlyPublishLensIds }).catch((e) =>
    thunkAPI.rejectWithValue(extractGQLError(e))
  )
);

export const deleteFormPage = createAsyncThunk<{ id: number }, { id: number }>(
  thunkName("deleteSectionGroup"),
  async ({ id }) => runGqlMutation(DeleteFormPageDocument, { id }).then(() => ({ id }))
);

export const updateFormPage = createAsyncThunk<UpdateFormPageMutation, FormPage>(
  thunkName("updateSectionGroup"),
  async (sectionGroup) => runGqlMutation(UpdateFormPageDocument, sectionGroup)
);

export const createFormPage = createAsyncThunk<CreateFormPageMutation, FormPage>(
  thunkName("createSectionGroup"),
  async (sectionGroup) => runGqlMutation(CreateFormPageDocument, sectionGroup)
);

export const deleteCell = createAsyncThunk<{ cellId: number; fieldId: number }, { cellId: number; fieldId: number }>(
  thunkName("deleteFormCell"),
  async ({ cellId, fieldId }) =>
    runGqlMutation(DeleteCellDocument, { cellId }).then(() => ({
      fieldId,
      cellId,
    }))
);

export const deleteSection = createAsyncThunk<
  { formId: number; formSectionId: number },
  { formId: number; formSectionId: number }
>(thunkName("deleteLensSection"), async ({ formId, formSectionId }) =>
  runGqlMutation(DeleteSectionDocument, { formId, formSectionId }).then(() => ({
    formId,
    formSectionId,
  }))
);

export const fetchChronologyConfig = createAsyncThunk<GetLensChronologyConfigQuery, { lensId: number }>(
  "lensEditor/fetchChronologyConfig",
  async ({ lensId }) => await runGqlQuery(GetLensChronologyConfigDocument, { lensId })
);

export const createChronologyConfig = createAsyncThunk<
  LensChronologyConfigView,
  {
    lensId: number;
    config: LensChronologyConfigInput;
  }
>("lensEditor/createChronologyConfig", async ({ lensId, config }) => {
  const configId = await runGqlMutation(CreateChronologyConfigDocument, {
    lensId,
    config,
  }).then((res) => res.lens.upsertChronologyConfig);

  return {
    config,
    id: configId,
    lensId,
    updatedAt: Date.now().toString(),
  };
});

export const updateChronologyConfig = createAsyncThunk<
  LensChronologyConfigView,
  {
    lensId: number;
    config: LensChronologyConfigInput;
    configId: number;
  }
>("lensEditor/updateChronologyConfig", async ({ lensId, config, configId }) => {
  await runGqlMutation(UpdateChronologyConfigDocument, {
    configId,
    lensId,
    config,
  });

  return {
    config,
    lensId,
    id: configId,
    updatedAt: Date.now().toString(),
  };
});

export const deleteChronologyConfig = createAsyncThunk<DeleteChronologyConfigMutation, { configId: number }>(
  "lensEditor/deleteChronologyConfig",
  async ({ configId }) => await runGqlMutation(DeleteChronologyConfigDocument, { id: configId })
);

export const fetchPageLabels = createAsyncThunk<GetPageLabelsQuery, { lensId: number }>(
  "lensEditor/fetchPageLabels",
  async ({ lensId }) => await runGqlQuery(GetPageLabelsDocument, { lensId })
);

export type SetSystemPageLabelsGroupsForLensDocumentMutationVariables = {
  lensId: number;
  labelGroups: SystemPageLabelGroupView[];
};

export const setSystemPageLabelGroupsForLens = createAsyncThunk<
  { labelGroups: SystemPageLabelGroupView[] },
  SetSystemPageLabelsGroupsForLensDocumentMutationVariables
>(
  "lensEditor/setSystemPageLabelGroupsForLens",
  async ({ lensId, labelGroups }) =>
    await runGqlMutation(SetSystemPageLabelGroupsForLensDocument, {
      lensId: lensId,
      labelGroups: labelGroups.map((lg) => lg.group),
    }).then(() => ({ labelGroups: labelGroups }))
);

export const fetchCaseDisplayGroups = createAsyncThunk<CaseDisplayGroup[], { lensId: number }>(
  "lensEditor/fetchCaseDisplayGroups",
  async ({ lensId }) =>
    await runGqlQuery(GetCaseDisplayGroupsDocument, { lensId }).then((r) => r.lens.caseDisplayGroups)
);

export const fetchCustomProperties = createAsyncThunk<
  {
    caseProperties: CustomCaseProperty[];
    documentProperties: CustomDocumentProperty[];
    calcProperties: CalcCaseProperty[];
    caseDisplayGroups: CaseDisplayGroup[];
  },
  { lensId: number }
>(
  "lensEditor/fetchCustomProperties",
  async ({ lensId }) =>
    await runGqlQuery(GetCustomPropertiesDocument, { lensId }).then((r) => ({
      caseProperties: r.lens.customProperties.listCustomCaseProperties,
      documentProperties: r.lens.customProperties.listCustomDocumentProperties,
      calcProperties: r.lens.customProperties.listCalcCaseProperties,
      caseDisplayGroups: r.lens.caseDisplayGroups,
    }))
);

export const deleteCaseDisplayGroup = createAsyncThunk<{ groupId: number }, { lensId: number; groupId: number }>(
  "lensEditor/deleteCaseDisplayGroup",
  async (props) => await runGqlMutation(DeleteCaseDisplayGroupDocument, props).then(() => props)
);

export const updateCaseDisplayGroups = createAsyncThunk<
  { groups: CaseDisplayGroupPutInput[] },
  {
    lensId: number;
    groups: CaseDisplayGroupPutInput[];
  }
>(
  "lensEditor/updateCaseDisplayGroups",
  async (props, thunkAPI) =>
    await runGqlMutation(UpdateCaseDisplayGroupsDocument, props)
      .then(() => props)
      .catch((err) => thunkAPI.rejectWithValue(extractGQLError(err)))
);

export const updateCaseDisplayGroupLocations = createAsyncThunk<
  number[],
  UpdateCaseDisplayGroupLocationsMutationVariables
>("lensEditor/updateCaseDisplayGroupLocations", async (props, thunkAPI) => {
  const res = await runGqlMutation(UpdateCaseDisplayGroupLocationsDocument, props).then(() =>
    forceGQLArray(props.rankedGroups)
  );
  await thunkAPI.dispatch(fetchCaseDisplayGroups({ lensId: props.lensId }));
  return res;
});

export const setCaseDisplayGroupTableItem = createAsyncThunk<
  CaseDisplayGroup[],
  SetCaseDisplayGroupTableItemMutationVariables
>(
  "lensEditor/setCaseDisplayGroupTableItem",
  async (props, thunkAPI) =>
    await runGqlMutation(SetCaseDisplayGroupTableItemDocument, props)
      .then(() => thunkAPI.dispatch(fetchCaseDisplayGroups({ lensId: props.lensId })).unwrap())
      .catch((err) => thunkAPI.rejectWithValue(extractGQLError(err)))
);

export const createCaseDisplayGroup = createAsyncThunk<CaseDisplayGroup, UpsertCaseDisplayGroupMutationVariables>(
  "lensEditor/createCaseDisplayGroup",
  async (props) =>
    await runGqlMutation(UpsertCaseDisplayGroupDocument, props).then((r) => r.lens.upsertCaseDisplayGroup)
);

export const updateCaseDisplayGroupWithItems = createAsyncThunk<
  CaseDisplayGroup,
  UpsertCaseDisplayGroupMutationVariables
>(
  "lensEditor/updateCaseDisplayGroupWithItems",
  async (props) =>
    await runGqlMutation(UpsertCaseDisplayGroupDocument, props).then((r) => r.lens.upsertCaseDisplayGroup)
);

export const upsertCalcProperty = createAsyncThunk<CalcCaseProperty, UpsertCaseCalcPropertyMutationVariables>(
  "lensEditor/upsertCalcProperty",
  async (input) =>
    await runGqlMutation(UpsertCaseCalcPropertyDocument, input).then(
      (r) => r.lens.customProperties.upsertCalcCaseProperty
    )
);

export const upsertCaseProperty = createAsyncThunk<CustomCaseProperty, UpsertCasePropertyMutationVariables>(
  "lensEditor/upsertCaseProperty",
  async (input) =>
    await runGqlMutation(UpsertCasePropertyDocument, input).then(
      (r) => r.lens.customProperties.upsertCustomCaseProperty
    )
);

export const renamePropSelectOption = createAsyncThunk<
  RenamePropSelectOptionMutationVariables,
  RenamePropSelectOptionMutationVariables
>(
  "lensEditor/renamePropSelectOption",
  async (input) => await runGqlMutation(RenamePropSelectOptionDocument, input).then(() => input)
);

export const upsertOutcomeStatusConfig = createAsyncThunk<
  LensOutcomeStatusConfigurationView,
  UpsertLensOutcomeStatusConfigurationMutationVariables
>(
  "lensEditor/upsertOutcomeStatusConfig",
  async (input) =>
    await runGqlMutation(UpsertLensOutcomeStatusConfigurationDocument, input).then((r) => {
      return {
        id: r.lens.upsertLensOutcomeStatusConfiguration,
        lensId: input.lensId,
        outcomeStatus: input.outcomeStatus,
        customName: input.customName,
        isHidden: input.isHidden,
      };
    })
);

export const deleteOutcomeStatusConfig = createAsyncThunk<{ id: number }, { id: number }>(
  "lensEditor/deleteOutcomeStatusConfig",
  async (input) =>
    await runGqlMutation(DeleteLensOutcomeStatusConfigurationDocument, input).then(() => {
      return {
        id: input.id,
      };
    })
);

export const upsertDocumentProperty = createAsyncThunk<CustomDocumentProperty, UpsertDocumentPropertyMutationVariables>(
  "lensEditor/upsertDocumentProperty",
  async (input) =>
    await runGqlMutation(UpsertDocumentPropertyDocument, input).then(
      (r) => r.lens.customProperties.upsertCustomDocumentProperty
    )
);

export const fetchLensDocumentStatus = createAsyncThunk<LensDocumentStatusNode, { lensId: number }>(
  "lensEditor/fetchLensDocumentStatus",
  async ({ lensId }) => await runGqlQuery(GetLensDocumentStatusDocument, { lensId }).then((d) => d.lensDocumentStatus)
);

export const upsertFieldTagConfig = createAsyncThunk<Exact<FieldTagConfigView>, FieldTagConfigView>(
  "lensEditor/upsertFieldTagConfig",
  async (input) => await runGqlMutation(UpsertFieldTagConfigDocument, input).then(() => input)
);

export const fetchTags = createAsyncThunk<{ tags: TagView[]; colors: TagColor[] }, { lensId: number }>(
  "lensEditor/fetchTags",
  async ({ lensId }) =>
    await runGqlQuery(GetTagsDocument, { lensId }).then((d) => ({
      tags: d.lens.formQueries.tags,
      colors: d.lens.formQueries.tagColors,
    }))
);

export const upsertTag = createAsyncThunk<Exact<TagView>, TagView & { lensId: number }>(
  "lensEditor/upsertTag",
  async (input) => await runGqlMutation(UpsertTagDocument, input).then(() => input)
);
